import {
  Component,
  Input,
  OnChanges,
  SimpleChanges,
  Output,
  EventEmitter,
  ChangeDetectionStrategy
} from "@angular/core";
import { AbstractControl, Validators } from "@angular/forms";

@Component({
    selector: "app-textbox",
    templateUrl: "./textbox.component.html",
    styleUrls: ["./textbox.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class TextboxComponent implements OnChanges {
  @Input() label: string = "";
  @Input() placeholder: string = "";
  @Input() control: AbstractControl | null = null;
  @Input() type: string = "text"; // For password, email, number, etc.
  @Input() required: boolean = false;
  @Input() disabled: boolean = false;
  @Input() min?: number;
  @Input() max?: number;
  @Input() step?: number;
  @Input() minLength?: number;
  @Input() maxLength?: number;
  @Input() dataId?: string;
  @Input() decimalSupport: boolean = false;
  @Input() regexPattern?: string;
  @Input() regexError?: string;

  @Output() valueChange = new EventEmitter<string>();

  ngOnChanges(changes: SimpleChanges) {
    if (changes["disabled"] && this.control) {
      this.disabled ? this.control.disable() : this.control.enable();
    }

    // Update validators when relevant properties change
    if (
      changes["required"] ||
      changes["min"] ||
      changes["max"] ||
      changes["type"] ||
      changes["minLength"] ||
      changes["maxLength"] ||
      changes["decimalSupport"] ||
      changes["regexPattern"]
    ) {
      this.updateValidators();
    }
  }

  onKeyPress(event: KeyboardEvent) {
    if (this.type === "number") {
      const key = event.key;
      const input = event.target as HTMLInputElement;
      const value = input.value;

      // Allow: backspace, delete, arrows, tab, escape, enter
      if (
        [
          "Backspace",
          "Delete",
          "Tab",
          "Escape",
          "Enter",
          "ArrowLeft",
          "ArrowRight",
        ].includes(key)
      ) {
        return true;
      }

      // Allow digits
      if (/^\d$/.test(key)) {
        // If decimal is supported, ensure no more than 2 digits after decimal point
        if (this.decimalSupport && value.includes(".")) {
          const parts = value.split(".");
          if (
            parts[1].length >= 2 &&
            input.selectionStart! > value.indexOf(".")
          ) {
            event.preventDefault();
            return false;
          }
        }
        return true;
      }

      // Allow decimal point if decimalSupport is enabled and not already present
      if (key === "." && this.decimalSupport && !value.includes(".")) {
        return true;
      }

      event.preventDefault();
      return false;
    }
    return true;
  }

  onNumberInput(event: Event) {
    const input = event.target as HTMLInputElement;
    const value = input.value;

    // Allow empty value
    if (!value) {
      this.control.setValue(null);
      // Mark control as invalid until it loses focus so that submit buttons remain disabled
      this.control.setErrors({ empty: true });
      this.control.markAsDirty();
      return;
    }

    if (this.decimalSupport && value.includes(".")) {
      const parts = value.split(".");

      // If the value ends with a dot or the user is still typing the fractional part (<2 digits)
      // keep it as a string for now.
      if (value.endsWith(".") || (parts[1] && parts[1].length < 2)) {
        this.control.setValue(value, { emitEvent: false });
        this.valueChange.emit(value);
        return;
      }
    }

    // At this point we either have an integer value or a complete decimal number (up to 2 decimal places)
    const numValue = Number(value);
    if (!isNaN(numValue)) {
      this.control.setValue(numValue, { emitEvent: false });
    } else {
      // Fallback – should rarely happen, but keeps the control in sync
      this.control.setValue(value, { emitEvent: false });
    }
    this.valueChange.emit(value);
  }

  /**
   * Enforce min/max once the user finishes editing the field (on blur).
   */
  onNumberBlur(event: Event) {
    const input = event.target as HTMLInputElement;
    let value = input.value;

    // Trim a trailing decimal point (e.g. "123.") before validation
    if (this.decimalSupport && value.endsWith(".")) {
      value = value.slice(0, -1);
      input.value = value;
    }

    // If the field is empty, set to min value if available
    if (value === "") {
      if (this.min !== undefined) {
        input.value = this.min.toString();
        this.control.setValue(this.min);
      } else {
        this.control.setValue(null);
      }
      // Clear temporary empty error
      this.control.setErrors(null);
      this.control.updateValueAndValidity();
      return;
    }

    const numValue = Number(value);
    if (isNaN(numValue)) {
      // If not a valid number, clear the field
      input.value = "";
      this.control.setValue(null);
      this.control.setErrors(null);
      this.control.updateValueAndValidity();
      return;
    }

    // Apply min/max constraints
    if (this.min !== undefined && numValue < this.min) {
      input.value = this.min.toString();
      this.control.setValue(this.min);
    } else if (this.max !== undefined && numValue > this.max) {
      input.value = this.max.toString();
      this.control.setValue(this.max);
    } else {
      this.control.setValue(numValue);
    }

    // Clear any temporary errors once value is settled
    this.control.setErrors(null);
    this.control.updateValueAndValidity();
  }

  onInputChange(event: Event) {
    const input = event.target as HTMLInputElement;
    const value = input.value;
    this.valueChange.emit(value);
  }

  private updateValidators() {
    const validators = [];

    // Required validator
    if (this.required) {
      validators.push(Validators.required);
    }

    // Type-specific validators
    switch (this.type) {
      case "email":
        validators.push(Validators.email);
        break;

      case "number":
        if (this.decimalSupport) {
          validators.push(Validators.pattern(/^-?\d*(\.\d{0,2})?$/));
        } else {
          validators.push(Validators.pattern(/^-?\d*$/));
        }
        if (this.min !== undefined) {
          validators.push(Validators.min(this.min));
        }
        if (this.max !== undefined) {
          validators.push(Validators.max(this.max));
        }
        break;

      case "password":
        // Only apply password validation if the field is empty (new password)
        if (!this.control.value) {
          validators.push(Validators.minLength(8));
          validators.push(
            Validators.pattern(
              /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/
            )
          );
        }
        break;

      case "text":
      default:
        if (this.minLength !== undefined) {
          validators.push(Validators.minLength(this.minLength));
        }
        if (this.maxLength !== undefined) {
          validators.push(Validators.maxLength(this.maxLength));
        }
        break;
    }

    // Add regex validation if pattern is provided
    if (this.regexPattern) {
      try {
        const cleanPattern = this.cleanRegexPattern(this.regexPattern);
        const regex = new RegExp(cleanPattern);
        validators.push(Validators.pattern(regex));
      } catch (error) {
        console.warn(
          "Invalid regex pattern provided:",
          this.regexPattern,
          error
        );
      }
    }

    // Preserve existing validators that are not type-specific
    if (this.control.validator) {
      const existingValidators = this.control.validator;
      // We'll add the existing validators to our new validators array
      // This is a simplified approach - in a more complex scenario, you might want to merge validators more carefully
      validators.push(existingValidators);
    }

    this.control.setValidators(validators);
    this.control.updateValueAndValidity();
  }

  /**
   * Clean regex pattern by removing delimiters if present
   */
  private cleanRegexPattern(pattern: string): string {
    // If pattern starts and ends with forward slashes, remove them
    if (pattern.startsWith("/") && pattern.endsWith("/")) {
      return pattern.slice(1, -1);
    }
    return pattern;
  }

  getErrorMessage(): string {
    if (this.control.errors && this.control.errors.required) {
      return "This field is required.";
    }

    if (this.control.errors && this.control.errors.email) {
      return "Please enter a valid email address.";
    }

    if (this.control.errors && this.control.errors.duplicateName) {
      return `Configuration name "${this.control.errors.duplicateName.value}" already exists. Please choose a different name.`;
    }

    if (this.control.errors && this.control.errors.spacesNotAllowed) {
      return "Configuration name cannot contain spaces. Please use dots, hyphens, or underscores instead.";
    }

    if (this.control.errors && this.control.errors.invalidConfigName) {
      return "Configuration name can only contain letters, numbers, dots, hyphens, and underscores.";
    }

    if (this.control.errors && this.control.errors.pattern) {
      // Check if this is a regex validation error (when regexPattern is provided)
      if (this.regexPattern && this.regexError) {
        return this.regexError;
      }

      switch (this.type) {
        case "number":
          return "Please enter a valid number.";
        case "password":
          // Only show password validation message if the field is empty
          return !this.control.value
            ? "Password must be at least 8 characters long and contain uppercase, lowercase, number and special character."
            : "";
        default:
          return "Please enter a valid value.";
      }
    }

    if (this.control.errors && this.control.errors.minlength) {
      const requiredLength = this.control.errors.minlength.requiredLength;
      return `Minimum length is ${requiredLength} characters.`;
    }

    if (this.control.errors && this.control.errors.maxlength) {
      const requiredLength = this.control.errors.maxlength.requiredLength;
      return `Maximum length is ${requiredLength} characters.`;
    }

    if (this.control.errors && this.control.errors.min) {
      return `Value must be greater than or equal to ${this.min}.`;
    }

    if (this.control.errors && this.control.errors.max) {
      return `Value must be less than or equal to ${this.max}.`;
    }

    return "Please enter a valid value.";
  }
}
